import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, MailWarning, MailX, Loader2 } from 'lucide-react';
import { authAPI } from '../services/api';

type VerifyState = 'verifying' | 'success' | 'expired' | 'invalid';

export default function VerifyEmailPage() {
  const { t } = useTranslation('auth');
  const [state, setState] = useState<VerifyState>('verifying');
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    const token = new URLSearchParams(window.location.search).get('token') || '';
    authAPI.verifyEmail(token)
      .then(() => setState('success'))
      .catch((err: any) => {
        const code = err?.response?.data?.code || '';
        setState(code === 'TOKEN_EXPIRED' ? 'expired' : 'invalid');
      });
  }, []);

  const states = {
    verifying: {
      icon: <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />,
      title: t('verifyEmail.verifying.title'),
      body: <p className="text-slate-500 text-center">{t('verifyEmail.verifying.body')}</p>,
      cta: null as React.ReactNode,
    },
    success: {
      icon: <CheckCircle2 className="w-8 h-8 text-green-600" />,
      title: t('verifyEmail.success.title'),
      body: <p className="text-slate-500 text-center mb-8">{t('verifyEmail.success.body')}</p>,
      cta: <Link to="/login" className="block w-full text-center py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700">{t('shared.loginLink')}</Link>,
    },
    expired: {
      icon: <MailWarning className="w-8 h-8 text-amber-500" />,
      title: t('verifyEmail.expired.title'),
      body: <div className="text-slate-500 text-center space-y-2 mb-8">
        <p>{t('verifyEmail.expired.body1')}</p>
        <p>{t('verifyEmail.expired.body2')} <strong>{t('verifyEmail.expired.newVerificationEmail')}</strong> {t('verifyEmail.expired.body2Tail')}</p>
      </div>,
      cta: <Link to="/login" className="block w-full text-center py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700">{t('shared.loginLink')}</Link>,
    },
    invalid: {
      icon: <MailX className="w-8 h-8 text-red-500" />,
      title: t('verifyEmail.invalid.title'),
      body: <p className="text-slate-500 text-center mb-8">{t('verifyEmail.invalid.body')}</p>,
      cta: <Link to="/login" className="block w-full text-center py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700">{t('shared.loginLink')}</Link>,
    },
  }[state];

  return (
    <div className="min-h-full bg-[var(--v-base-bg)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8">
          <ArrowLeft className="w-4 h-4" />{t('shared.backToLogin')}
        </Link>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">{states.icon}</div>
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-2">{states.title}</h1>
          {states.body}
          {states.cta}
        </div>
      </div>
    </div>
  );
}
